/**
 * mock-cross-sell.ts — Frontend mock data for the Cross-Sell Opportunity List (dev/demo mode).
 *
 * Derives cross-sell opportunities from existing mockProfiles.ts data — no new mock data.
 * In production, CrossSellPage fetches from GET /api/cross-sell/opportunities?product=heloc.
 *
 * Data flow:
 * 1. Iterate MOCK_MEMBERS (master dev member list)
 * 2. For each member, call getMockProfile() → read propensityScores.scores + aireSignals
 * 3. Normalize scores from 0-1 float to 0-100 integer
 * 4. Map AIRE signal types to readable SHAP labels
 * 5. Filter out members without propensity scores, sort by score desc
 */

import { MOCK_MEMBERS } from './mock-members';
import { getMockProfile } from '../pages/member360/mockProfiles';

// ---- Product type ----

export type ProductType = 'autoLoan' | 'mortgage' | 'cd' | 'heloc' | 'personalLoan';

// ---- Product display labels ----

export const PRODUCT_LABELS: Record<ProductType, string> = {
  autoLoan: 'Auto Loan',
  mortgage: 'Mortgage',
  cd: 'CD / Savings',
  heloc: 'HELOC',
  personalLoan: 'Personal Loan',
};

// ---- Ordered product type list for UI dropdowns ----

export const PRODUCT_TYPES: ProductType[] = [
  'autoLoan',
  'mortgage',
  'cd',
  'heloc',
  'personalLoan',
];

// ---- SHAP signal type → readable label map ----

export const SHAP_LABEL_MAP: Record<string, string> = {
  heloc_intent: 'Strong HELOC inquiry signal',
  cd_ladder_opportunity: 'Idle savings — CD ladder candidate',
  cd_renewal_likely: 'CD approaching maturity',
  idle_cash_opportunity: 'High idle savings balance',
  mortgage_intent_early: 'Early mortgage interest signal',
  churn_risk_elevated: 'Retention opportunity',
  heloc_draw_intent: 'HELOC draw intent detected',
  business_banking_intent: 'Business banking inquiry signal',
  delinquency_flag: 'Delinquency risk factor',
};

// ---- Cross-sell opportunity interface ----

export interface CrossSellOpportunity {
  memberId: string;
  memberName: string;
  memberNumber: string;
  lifecycleStage: string;
  /** Propensity score normalized to 0-100 integer */
  propensityScore: number;
  propensityTier: 'high' | 'medium' | 'low';
  /** Top AIRE signal label, or null if no signals */
  topShapFactor: string | null;
  /** ISO timestamp when scores were generated, or null */
  scoredAt: string | null;
}

// ---- Mock data function ----

/**
 * Derives cross-sell opportunities for the given product from existing mockProfiles data.
 *
 * @param product - One of the ProductType values
 * @returns Sorted array of CrossSellOpportunity (score descending), members without scores excluded
 */
export function getMockCrossSellOpportunities(product: ProductType): CrossSellOpportunity[] {
  const results: CrossSellOpportunity[] = [];

  for (const member of MOCK_MEMBERS) {
    const profile = getMockProfile(member.memberId);

    // Skip members without propensity scores
    const scores = profile.propensityScores?.scores;
    if (scores == null) {
      continue;
    }

    // Read raw 0-1 float score for the selected product
    const rawScore = scores[product as keyof typeof scores];
    if (typeof rawScore !== 'number') {
      continue;
    }

    // Normalize 0-1 float to 0-100 integer
    const propensityScore = Math.round(rawScore * 100);

    // Compute tier
    const propensityTier: 'high' | 'medium' | 'low' =
      propensityScore >= 70 ? 'high' : propensityScore >= 40 ? 'medium' : 'low';

    // Map top AIRE signal type to readable label
    const signals = profile.aireSignals?.signals;
    let topShapFactor: string | null = null;
    if (signals != null && signals.length > 0) {
      const rawSignalType = signals[0]?.signalType ?? null;
      if (rawSignalType !== null) {
        topShapFactor = SHAP_LABEL_MAP[rawSignalType] ?? rawSignalType;
      }
    }

    // scoredAt from propensity scores
    const scoredAt: string | null =
      typeof scores.scoredAt === 'string' ? scores.scoredAt : null;

    results.push({
      memberId: member.memberId,
      memberName: member.displayName,
      memberNumber: member.memberNumber,
      lifecycleStage: member.lifecycleStage,
      propensityScore,
      propensityTier,
      topShapFactor,
      scoredAt,
    });
  }

  // Sort by propensityScore descending
  results.sort((a, b) => b.propensityScore - a.propensityScore);

  return results;
}
